/**
 * Snooze's quick choices (architecture §9 `mail.snooze`), in the person's local time: later today
 * (18:00, or three hours from now once it is past 15:00), tomorrow at 8:00, and next week (Monday
 * at 8:00). Pure, so the times are tested without a clock.
 */
export interface SnoozeChoice {
  key: "later" | "tomorrow" | "nextWeek";
  label: string;
  until: Date;
}

const at = (base: Date, days: number, hour: number) => {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  return d;
};

export function laterToday(now: Date): Date {
  const evening = at(now, 0, 18);
  if (evening.getTime() - now.getTime() >= 3 * 3600 * 1000) return evening;
  const later = new Date(now.getTime() + 3 * 3600 * 1000);
  later.setMinutes(0, 0, 0);
  return later;
}

export function nextMonday(now: Date): Date {
  const days = (8 - now.getDay()) % 7;
  return at(now, days === 0 ? 7 : days, 8);
}

const WHEN = new Intl.DateTimeFormat("en", { weekday: "short", hour: "numeric", minute: "2-digit" });
const FULL = new Intl.DateTimeFormat("en", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function snoozeChoices(now: Date): SnoozeChoice[] {
  const later = laterToday(now);
  const tomorrow = at(now, 1, 8);
  const week = nextMonday(now);
  return [
    { key: "later", label: `Later today · ${WHEN.format(later)}`, until: later },
    { key: "tomorrow", label: `Tomorrow · ${WHEN.format(tomorrow)}`, until: tomorrow },
    { key: "nextWeek", label: `Next week · ${WHEN.format(week)}`, until: week },
  ];
}

/** "Oct 13, 8:00 AM": how a snoozed thread says when it comes back. */
export function snoozeLabel(until: Date): string {
  return FULL.format(until);
}

/** A `datetime-local` value ("2026-10-13T08:00") as a local Date; null when it is not one. */
export function parseLocalDateTime(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (m === null) return null;
  const [, y, mo, d, h, mi] = m.map(Number) as [number, number, number, number, number, number];
  const date = new Date(y, mo - 1, d, h, mi, 0, 0);
  return Number.isNaN(date.getTime()) || date.getMonth() !== mo - 1 ? null : date;
}

/** A local Date as a `datetime-local` value. */
export function toLocalDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
